/**
 * ValidatedInput - Auto-integrated Input Field
 *
 * Smart input component that automatically integrates with react-hook-form context.
 * Includes built-in validation, character counter, error display, and helper text.
 *
 * Features:
 * - Automatic react-hook-form registration
 * - Character counter (optional)
 * - Inline error display with icons
 * - Helper text support
 * - Semantic color tokens
 * - Responsive design
 *
 * @module validated-input
 */

'use client';

import * as React from 'react';
import { useFormContext, type RegisterOptions } from 'react-hook-form';
import { LuTriangleAlert, LuCircleHelp } from 'react-icons/lu';

import { Input, type InputProps } from '../../input';
import { Field } from '../../form'; // Use Chakra UI Field for SSR-safe ID generation
import { Box } from '../../box';
import { Text } from '../../text';
import { HStack, VStack } from '../../stack';
import { Tooltip } from '../../ui/tooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * ValidatedInput props
 */
export interface ValidatedInputProps extends Omit<InputProps, 'name'> {
  /**
   * Field name (required for react-hook-form)
   */
  name: string;

  /**
   * Field label
   */
  label?: string;

  /**
   * Helper text (shown below input)
   */
  helperText?: string;

  /**
   * Tooltip text (shown next to label with info icon)
   */
  tooltipText?: string;

  /**
   * Show character counter
   * @default true (if maxLength is provided)
   */
  showCharacterCount?: boolean;

  /**
   * Validation rules (react-hook-form)
   */
  rules?: RegisterOptions;

  /**
   * Whether field is required
   * @default false
   */
  required?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ValidatedInput - Smart input with automatic validation
 *
 * Automatically integrates with react-hook-form context and provides
 * comprehensive validation feedback.
 *
 * @example Basic usage
 * ```tsx
 * <FormProvider {...methods}>
 *   <ValidatedInput
 *     name="email"
 *     label="Email Address"
 *     type="email"
 *     required
 *     helperText="We'll never share your email"
 *   />
 * </FormProvider>
 * ```
 *
 * @example With character counter
 * ```tsx
 * <ValidatedInput
 *   name="name"
 *   label="Full Name"
 *   maxLength={100}
 *   showCharacterCount
 *   tooltipText="Enter your legal name"
 * />
 * ```
 *
 * @example With custom validation
 * ```tsx
 * <ValidatedInput
 *   name="username"
 *   label="Username"
 *   rules={{
 *     required: 'Username is required',
 *     minLength: { value: 3, message: 'Minimum 3 characters' },
 *     pattern: { value: /^[a-z0-9_]+$/, message: 'Only lowercase letters, numbers, and underscores' }
 *   }}
 * />
 * ```
 */
export const ValidatedInput = React.forwardRef<HTMLInputElement, ValidatedInputProps>(
  (
    {
      name,
      label,
      helperText,
      tooltipText,
      showCharacterCount,
      rules,
      required = false,
      maxLength,
      ...inputProps
    },
    ref
  ) => {
    // ============================================================================
    // Form Context
    // ============================================================================

    const formContext = useFormContext();
    const {
      register,
      formState: { errors },
      watch,
    } = formContext || {};

    // Defensive: If no form context, render basic input
    if (!formContext) {
      return (
        <VStack gap={2} align="stretch">
          {label && (
            <Text fontSize="sm" fontWeight="medium" color="fg.default">
              {label}
              {required && (
                <Text as="span" color="fg.error" marginLeft={1}>
                  *
                </Text>
              )}
            </Text>
          )}
          <Input ref={ref} name={name} required={required} maxLength={maxLength} {...inputProps} />
          {helperText && (
            <Text fontSize="xs" color="fg.subtle">
              {helperText}
            </Text>
          )}
        </VStack>
      );
    }

    // ============================================================================
    // Derived State
    // ============================================================================

    const error = errors?.[name];
    const value = watch?.(name) || '';
    const valueLength = String(value).length;

    const shouldShowCharacterCount =
      showCharacterCount !== false && maxLength !== undefined && maxLength > 0;

    // Warning threshold (90% of maxLength)
    const warningThreshold = maxLength ? maxLength * 0.9 : 0;
    const isNearLimit = maxLength && valueLength > warningThreshold;

    // ============================================================================
    // Registration
    // ============================================================================

    const registration = register(name, {
      required: required ? `${label || name} is required` : false,
      maxLength: maxLength
        ? {
            value: maxLength,
            message: `Maximum ${maxLength} characters allowed`,
          }
        : undefined,
      ...rules,
    });

    // ============================================================================
    // Render
    // ============================================================================

    return (
      <Field.Root invalid={!!error} required={required}>
        {/* Label Row */}
        {label && (
          <HStack justifyContent="space-between" marginBottom={2}>
            <Box as={Field.Label}>
              <HStack gap={1} align="center">
                <Text fontSize="sm" fontWeight="medium" color="fg.default">
                  {label}
                </Text>
                {required && (
                  <Text fontSize="sm" color="fg.error">
                    *
                  </Text>
                )}
                {tooltipText && (
                  <Tooltip content={tooltipText}>
                    <Box as="span" display="inline-flex" cursor="help">
                      <LuCircleHelp size={14} color="var(--chakra-colors-fg-muted)" />
                    </Box>
                  </Tooltip>
                )}
              </HStack>
            </Box>

            {/* Character Counter */}
            {shouldShowCharacterCount && (
              <Text
                fontSize="xs"
                color={isNearLimit ? 'orange.500' : 'fg.muted'}
                fontWeight={isNearLimit ? 'medium' : 'normal'}
              >
                {valueLength}/{maxLength}
              </Text>
            )}
          </HStack>
        )}

        {/* Input Field */}
        <Input
          {...registration}
          {...inputProps}
          ref={ref}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : helperText ? `${name}-helper` : undefined}
        />

        {/* Error / Helper Text */}
        <VStack gap={1} align="flex-start" marginTop={1}>
          {error && (
            <HStack gap={1}>
              <LuTriangleAlert size={12} color="var(--chakra-colors-fg-error)" />
              <Box as={Field.ErrorText} id={`${name}-error`} fontSize="sm">
                {error.message as string}
              </Box>
            </HStack>
          )}

          {helperText && !error && (
            <Box as={Field.HelperText} id={`${name}-helper`} fontSize="xs" color="fg.subtle">
              {helperText}
            </Box>
          )}
        </VStack>
      </Field.Root>
    );
  }
);

ValidatedInput.displayName = 'ValidatedInput';
